if (!customElements.get('countdown-timer')) {
  customElements.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      disconnectedCallback() {
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = null;
        }
      }

      init() {
        this.MS_IN_SEC = 1000;
        this.MS_IN_MIN = 60 * this.MS_IN_SEC;
        this.MS_IN_HRS = 60 * this.MS_IN_MIN;
        this.MS_IN_DAY = 24 * this.MS_IN_HRS;

        this.selectorMapping = {
          day: '[data-timer-day]',
          hour: '[data-timer-hour]',
          minute: '[data-timer-minute]',
          second: '[data-timer-second]',
        };
        this.units = ['hour', 'minute', 'second'];
        this.type = this.dataset.type;
        this.duration = this.dataset.duration;
        this.loop = false;
        this.shouldAddLeadingZero = true;
        this.isAdaptive = this.dataset.displayMode === 'adaptive';
        this.hoursThreshold = this.parseHoursThreshold(this.dataset.hoursThreshold);
        this.fixedMode = null;

        this.readUnitLabels();
        this.initCountdown();
      }

      parseHoursThreshold(value) {
        const parsed = parseInt(value, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 36;
      }

      readUnitLabels() {
        this.labelDay = this.dataset.labelDays || 'days';
        this.labelHour = this.dataset.labelHours || 'hours';
        this.labelMinute = this.dataset.labelMinutes || 'minutes';
        this.labelSecond = this.dataset.labelSeconds || 'seconds';
      }

      initCountdown() {
        switch (this.type) {
          case 'fixed_time':
            const countdownData = this.dataset.dueDate;
            if (countdownData) {
              if (window.GcwCountdownMath && window.GcwCountdownMath.parseDueDate) {
                this.targetTime = window.GcwCountdownMath.parseDueDate(countdownData);
              } else {
                this.targetTime = new Date(countdownData).getTime();
              }
            }
            break;
          default: // Evergreen.
            this.targetTime = this.getEvergreenDueDate().getTime();
            break;
        }

        if (!this.targetTime) {
          return;
        }

        this.startTime = Date.now();
        this.fetchDomElements();

        // Theme editor: show the timer once. A live interval fights section re-renders while
        // editing settings and can make the sidebar look like it crashed or went blank.
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          const remainingTime = Math.max(0, this.targetTime - this.startTime);
          if (remainingTime > 0) {
            this.updateDisplay(remainingTime);
          }
          this.classList.remove('hidden');
          return;
        }

        if (this.targetTime > this.startTime) {
          this.beginCountdown();
        }
      }

      fetchDomElements() {
        this.elements = {};
        this.elements.day = this.querySelector(this.selectorMapping.day);

        if (this.elements.day) {
          this.units = ['day', 'hour', 'minute', 'second'];
          if (!this.isAdaptive) {
            this.fixedMode = 'days';
          }
        } else {
          this.units = ['hour', 'minute', 'second'];
          this.fixedMode = 'hours';
        }

        this.units.forEach((unit) => {
          this.elements[unit] = this.querySelector(this.selectorMapping[unit]);
        });

        this.daySeparator = this.querySelector('[data-timer-day-sep]');
        this.srLive = this.querySelector('[data-timer-sr-live]');
        this.visualRoot = this.querySelector('[data-timer-visual]');
      }

      beginCountdown() {
        const remainingTime = this.targetTime - Date.now();
        if (remainingTime > 0) {
          this.updateDisplay(remainingTime);
        }

        this.intervalId = setInterval(() => {
          const tickRemaining = this.targetTime - Date.now();
          if (tickRemaining <= 0) {
            this.terminateCountdown();
          } else {
            this.updateDisplay(tickRemaining);
          }
        }, 1000);
        this.classList.remove('hidden');
      }

      getDisplayMode(remainingMs) {
        if (this.fixedMode) {
          return this.fixedMode;
        }

        if (window.GcwCountdownMath) {
          return window.GcwCountdownMath.resolveDisplayMode(this, remainingMs);
        }

        if (this.isAdaptive && this.elements.day) {
          return remainingMs > this.hoursThreshold * this.MS_IN_HRS ? 'days' : 'hours';
        }
        return 'hours';
      }

      applyDisplayMode(mode) {
        if (window.GcwCountdownMath) {
          window.GcwCountdownMath.applyDisplayMode(this, mode);
          return;
        }

        if (!this.elements.day) {
          return;
        }

        const isDaysMode = mode === 'days';

        if (this.isAdaptive) {
          this.classList.toggle('is-days-mode', isDaysMode);
          this.classList.toggle('is-hours-mode', !isDaysMode);

          if (window.GcwCountdownMath && window.GcwCountdownMath.syncTimerBoxMode) {
            window.GcwCountdownMath.syncTimerBoxMode(this, mode);
          } else {
            const box = this.closest('.op-e__timer-box--adaptive');
            if (box) {
              box.classList.toggle('is-days-mode', isDaysMode);
              box.classList.toggle('is-hours-mode', !isDaysMode);
            }
          }

          if (this.daySeparator) {
            this.daySeparator.hidden = !isDaysMode;
          }
          this.elements.day.hidden = !isDaysMode;
          return;
        }

        this.classList.add('is-days-mode');
        this.classList.remove('is-hours-mode');
        if (window.GcwCountdownMath && window.GcwCountdownMath.syncTimerBoxMode) {
          window.GcwCountdownMath.syncTimerBoxMode(this, 'days');
        }
      }

      updateDisplay(remainingTime) {
        const mode = this.getDisplayMode(remainingTime);
        this.applyDisplayMode(mode);

        const timeComponents = this.computeTimeComponents(remainingTime, mode);
        this.units.forEach((unit) => {
          if (this.elements[unit] && timeComponents[unit] !== undefined) {
            this.elements[unit].textContent = this.formatTimeComponent(timeComponents[unit]);
          }
        });

        this.updateAccessiblePhrase(timeComponents, mode);
      }

      updateAccessiblePhrase(timeComponents, mode) {
        if (!this.srLive) {
          return;
        }

        this.srLive.textContent = this.formatAccessiblePhrase(timeComponents, mode);
      }

      formatAccessiblePhrase(components, mode) {
        const parts = [];

        if (mode === 'days' && components.day !== undefined) {
          parts.push(`${components.day} ${this.labelDay}`);
        }

        if (components.hour !== undefined) {
          parts.push(`${components.hour} ${this.labelHour}`);
        }

        if (components.minute !== undefined) {
          parts.push(`${components.minute} ${this.labelMinute}`);
        }

        if (components.second !== undefined) {
          parts.push(`${components.second} ${this.labelSecond}`);
        }

        return parts.join(', ');
      }

      computeTimeComponents(ms, mode) {
        if (window.GcwCountdownMath) {
          return window.GcwCountdownMath.computeTimeComponents(ms, mode);
        }

        if (mode === 'days') {
          return {
            day: Math.floor(ms / this.MS_IN_DAY),
            hour: Math.floor((ms % this.MS_IN_DAY) / this.MS_IN_HRS),
            minute: Math.floor((ms % this.MS_IN_HRS) / this.MS_IN_MIN),
            second: Math.floor((ms % this.MS_IN_MIN) / this.MS_IN_SEC),
          };
        }

        const totalHours = Math.floor(ms / this.MS_IN_HRS);
        return {
          hour: totalHours,
          minute: Math.floor((ms % this.MS_IN_HRS) / this.MS_IN_MIN),
          second: Math.floor((ms % this.MS_IN_MIN) / this.MS_IN_SEC),
        };
      }

      formatTimeComponent(value) {
        if (window.GcwCountdownMath) {
          return window.GcwCountdownMath.formatTimeComponent(value);
        }

        return this.shouldAddLeadingZero && value < 10 ? `0${value}` : value.toString();
      }

      terminateCountdown() {
        clearInterval(this.intervalId);
        if (this.loop) {
          this.beginCountdown();
        } else {
          if (this.srLive) {
            this.srLive.textContent = '';
          }
          this.classList.add('hidden');
        }
      }

      resetCountdown() {
        clearInterval(this.intervalId);
        const resetUnits = this.elements.day ? ['day', 'hour', 'minute', 'second'] : ['hour', 'minute', 'second'];
        resetUnits.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = '00';
          }
        });
        if (this.srLive) {
          this.srLive.textContent = '';
        }
      }

      getEvergreenDueDate() {
        let intervalHrs;
        const now = new Date();
        const dueDate = new Date(now);

        switch (this.duration) {
          case 'every_month':
            dueDate.setMonth(dueDate.getMonth() + 1); // Next month.
            dueDate.setDate(1); // Start from the 1st day of the next month.
            dueDate.setHours(0, 0, 0, 0);
            return dueDate;
          case 'every_week':
            const dayOfWeek = now.getDay();
            const daysUntilNextWeek = 7 - dayOfWeek;
            dueDate.setDate(now.getDate() + daysUntilNextWeek); // Move to next Sunday.
            dueDate.setHours(0, 0, 0, 0); // Set the time to 00:00:00.
            return dueDate;
          case 'every_2_hrs':
            intervalHrs = 2;
            break;
          case 'every_6_hrs':
            intervalHrs = 6;
            break;
          case 'every_12_hrs':
            intervalHrs = 12;
            break;
          case 'every_24_hrs':
            intervalHrs = 24;
            break;
        }

        const currentHour = now.getHours();

        // Possible start points based on the interval.
        const startPoints = [];
        for (let i = 0; i < 24; i += intervalHrs) {
          startPoints.push(i);
        }

        // Find the next start point.
        for (let i = 0; i < startPoints.length; i++) {
          if (
            currentHour < startPoints[i] ||
            (currentHour === startPoints[i] && now.getMinutes() === 0 && now.getSeconds() === 0)
          ) {
            dueDate.setHours(startPoints[i], 0, 0, 0);
            return dueDate;
          }
        }

        // If no future point today, roll over to the first point tomorrow.
        dueDate.setDate(dueDate.getDate() + 1);
        dueDate.setHours(startPoints[0], 0, 0, 0);
        return dueDate;
      }
    }
  );
}
